import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'state/store';
import { 
    fetchBotStatus, 
    updateWhatsAppConfig,
    clearBotStatus,
    WhatsAppConfig 
} from 'state/botStatus/botStatusSlice';
import { Logger } from 'utils/Logger';

// Global singleton to track if Bot Status data is already initialized
let isBotStatusInitialized = false;
let globalIntervalRef: NodeJS.Timeout | null = null;

export const useWhatsAppData = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: RootState) => state.authentication);
    const { 
        botStatus, 
        loading, 
        error, 
        lastFetched, 
        whatsappDeducedStatus: deducedStatus, 
        isQRCodeOutdated 
    } = useSelector((state: RootState) => state.botStatus);
    
    // Extract whatsapp config from botStatus for backward compatibility
    const config = botStatus?.whatsapp || null;
    
    const fetchedCompanyIdRef = useRef<string | null>(null);

    // Function to start auto-refresh (global singleton)
    const startAutoRefresh = useCallback(() => {
        // Clear existing global interval first
        if (globalIntervalRef) {
            clearInterval(globalIntervalRef);
            globalIntervalRef = null;
        }

        // Determine refresh interval based on status
        let refreshInterval = 30000;
        if (deducedStatus === 'QRCODE') {
            refreshInterval = 7000; // Refresh more frequently when waiting for QR scan
        } else if (deducedStatus === 'ONLINE') {
            refreshInterval = 10000; // Keep existing for ONLINE
        }
        
        Logger.info("useWhatsAppData.ts : startAutoRefresh()", `Setting refresh interval to ${refreshInterval}ms (${deducedStatus} status)`);
        
        globalIntervalRef = setInterval(() => {
            if (user?.companyId) {
                Logger.info("useWhatsAppData.ts : startAutoRefresh()", "Auto-refreshing bot status data");
                dispatch(fetchBotStatus(user.companyId));
            }
        }, refreshInterval);
        
        Logger.info("useWhatsAppData.ts : startAutoRefresh()", `Global interval set with ID: ${globalIntervalRef}`);
    }, [deducedStatus, user?.companyId, dispatch]);

    // Function to stop auto-refresh (global singleton)
    const stopAutoRefresh = useCallback(() => {
        if (globalIntervalRef) {
            Logger.info("useWhatsAppData.ts : stopAutoRefresh()", `Stopping global interval with ID: ${globalIntervalRef}`);
            clearInterval(globalIntervalRef);
            globalIntervalRef = null;
            Logger.info("useWhatsAppData.ts : stopAutoRefresh()", "Global auto-refresh stopped");
        }
    }, []);

    // Function to manually refresh data
    const refreshData = useCallback(() => {
        if (user?.companyId) {
            Logger.info("useWhatsAppData.ts : refreshData()", "Manually refreshing bot status data");
            dispatch(fetchBotStatus(user.companyId));
        }
    }, [user?.companyId, dispatch]);

    // Function to update WhatsApp configuration
    const updateConfig = useCallback((configData: Partial<WhatsAppConfig>) => {
        if (user?.companyId) {
            Logger.info("useWhatsAppData.ts : updateConfig()", "Updating WhatsApp configuration");
            return dispatch(updateWhatsAppConfig({ companyId: user.companyId, config: configData }));
        }
        return Promise.reject(new Error('No company ID available'));
    }, [user?.companyId, dispatch]);

    // Function to clear bot status data
    const clearData = useCallback(() => {
        Logger.info("useWhatsAppData.ts : clearData()", "Clearing bot status data");
        dispatch(clearBotStatus());
        stopAutoRefresh();
        isBotStatusInitialized = false;
    }, [dispatch, stopAutoRefresh]);

    // Main effect to initialize and manage auto-refresh (singleton)
    useEffect(() => {
        Logger.info("useWhatsAppData.ts : useEffect", `Dependencies changed - user: ${user?.companyId}, status: "${deducedStatus}", isOutdated: ${isQRCodeOutdated}, initialized: ${isBotStatusInitialized}`);
        
        // Clear data when user changes
        if (fetchedCompanyIdRef.current && fetchedCompanyIdRef.current !== user?.companyId) {
            Logger.info("useWhatsAppData.ts : useEffect", "User changed, clearing previous data");
            clearData();
            fetchedCompanyIdRef.current = null;
            isBotStatusInitialized = false;
        }

        // Fetch data when user is available but no data exists
        if (user?.companyId && !botStatus && !loading && fetchedCompanyIdRef.current !== user.companyId) {
            Logger.info("useWhatsAppData.ts : useEffect", `Fetching bot status for companyId: ${user.companyId}`);
            dispatch(fetchBotStatus(user.companyId));
            fetchedCompanyIdRef.current = user.companyId;
        }

        // Start auto-refresh only once globally when user is available
        if (user?.companyId && !isBotStatusInitialized) {
            Logger.info("useWhatsAppData.ts : useEffect", "Initializing global auto-refresh");
            isBotStatusInitialized = true;
            startAutoRefresh();
        }

        // Cleanup on unmount
        return () => {
            Logger.info("useWhatsAppData.ts : useEffect", "Component unmounting, but keeping global interval");
        };
    }, [user?.companyId, dispatch, startAutoRefresh, stopAutoRefresh, clearData, botStatus, loading]);

    // Effect to restart interval when status changes (only if already initialized)
    useEffect(() => {
        if (isBotStatusInitialized && user?.companyId) {
            Logger.info("useWhatsAppData.ts : status effect", `Status changed to ${deducedStatus}, restarting global interval`);
            startAutoRefresh();
        }
    }, [deducedStatus, user?.companyId, startAutoRefresh]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Don't stop the global interval on component unmount
            Logger.info("useWhatsAppData.ts : cleanup", "Component unmounting, keeping global interval active");
        };
    }, []);

    return {
        // WhatsApp specific data (backward compatible)
        config,
        deducedStatus,
        isQRCodeOutdated,
        
        // Full bot status
        botStatus,
        emailConfig: botStatus?.email || null,
        gsmWorkerPool: botStatus?.gsmWorkerPool || null,
        
        // Common
        loading,
        error,
        lastFetched,
        refreshData,
        updateConfig,
        clearData,
        hasConfig: !!config,
        hasBotStatus: !!botStatus,
        hasUser: !!user,
        companyId: user?.companyId,
        isAutoRefreshRunning: globalIntervalRef !== null
    };
}; 